import { css, cx } from 'emotion';
import {
	darkGrey,
	orange,
	aqua,
	pink,
	purple,
	burnedOrange,
	desktopMaxWidthBreakpoint,
	headingLarge,
	headingMedium,
	textBlock,
	cardSection,
	headingXLarge,
	headingSmallLight,
	label,
	museo500,
	museo900,
	tabletLandscapeMaxWidthBreakpoint,
	tabletPortraitMaxWidthBreakpoint,
	mobileMaxWidthBreakpoint,
} from 'inside-out/global-styles';

import QuoteBG from 'inside-out/images/about-eating-disorders/quote-bg.svg';
import MediaIcon from 'inside-out/images/media-icon.svg';
import BlockquoteBG from 'inside-out/images/about-eating-disorders/blockquote-bg.svg';

const conditionsHash = {
	'anorexia-nervosa': orange,
	'bulimia-nervosa': pink,
	'binge-eating-disorder': aqua,
};

export const titleStyle = cx(
	headingLarge,
	css({
		marginBottom: '24px',
	})
);

export const statisticsWrapStyle = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
	[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
		display: 'table',
	},
});

export const statisticsLeftStyle = css({
	width: '354px',
	boxSizing: 'content-box',
	'& h2': css({
		...museo900,
		fontSize: '64px',
		lineHeight: '77px',
		fontWeight: 'normal',
		margin: 0,
		color: burnedOrange,
	}),
	'& p': cx(
		textBlock,
		css({
			margin: 0,
			color: darkGrey,
		})
	),
	[`@media (max-width: ${tabletLandscapeMaxWidthBreakpoint}px)`]: {
		'& h2': cx(
			headingLarge,
			css({
				color: burnedOrange,
			})
		),
	},
	[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
		display: 'table-row',
		width: '100%',
		'& p': {
			marginBottom: '60px',
		},
	},
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		width: '100%',
		'& p': {
			marginBottom: '24px',
		},
	},
});

export const statisticsCenterStyle = css({
	margin: '0 25px',
	textAlign: 'center',
	'& img': {
		width: '297px',
	},
	[`@media (max-width: ${tabletLandscapeMaxWidthBreakpoint}px)`]: {
		'& img': {
			width: '234px',
		},
	},
	[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
		display: 'table-cell',
		verticalAlign: 'top',
		width: '50%',
	},
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		display: 'none',
	},
});

export const statisticsRightStyle = css({
	width: '300px',
	'& ul': {
		padding: 0,
		margin: 0,
	},
	[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
		display: 'table-cell',
		verticalAlign: 'top',
		width: '50%',
	},
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		width: '100%',
	},
});

export const greyTitleStyle = cx(
	titleStyle,
	css({
		color: '#fff',
		maxWidth: '600px',
		[`@media (max-width: ${tabletLandscapeMaxWidthBreakpoint}px)`]: {
			maxWidth: 'initial',
		},
	})
);

export const greyStatisticsWrapStyle = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
	'& > div': {
		width: '320px',
	},
	'& p': {
		color: '#fff',
	},
	[`@media (max-width: ${tabletLandscapeMaxWidthBreakpoint}px)`]: {
		flexDirection: 'column',
		'& > div': {
			marginTop: '60px',
			width: '100%',
		},
	},
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		'& > div': {
			marginTop: '30px',
		},
	},
});

export const greyStatisticsColumnStyle = condition =>
	css({
		'& em, & strong': {
			color: conditionsHash[condition],
			fontStyle: 'normal',
			fontWeight: 'normal',
		},
		'& > em': {
			...museo900,
			fontSize: '58px',
			lineHeight: '1',
			fontWeight: 'normal',
			'& > span': {
				...museo500,
				fontSize: '18px',
				lineHeight: '22px',
				color: '#fff',
				marginLeft: '9px',
				letterSpacing: '0.5px',
				top: '-4px',
				position: 'relative',
				display: 'inline-block',
			},
		},

		'& p': {
			margin: '0',
			color: '#fff',
		},
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			'& > em': {
				fontSize: '30px',
				lineHeight: '36px',
			},
		},
	});

export const statisticStyle = condition => {
	return css({
		listStyle: 'none',
		'& h2': cx(
			headingLarge,
			css({
				fontWeight: 'normal',
				marginBottom: '0',
			})
		),
		'& p': {
			margin: '0',
		},

		'& h2, & p span': {
			color: conditionsHash[condition],
		},
		'&:not(:last-child)': {
			marginBottom: '24px',
		},
	});
};

export const textStyle = cx(
	textBlock,
	css({
		'& p': {
			maxWidth: '800px',
		},
	})
);

export const experienceTitleStyle = cx(
	headingMedium,
	css({
		fontWeight: 'normal',
	})
);

export const experienceCardInnerStyle = css({
	position: 'absolute',
	bottom: '24px',
	left: '24px',
});

export const experienceCardStyle = (background, hasVideo) =>
	css({
		borderRadius: '6px',
		maxWidth: '376px',
		height: '212px',
		position: 'relative',
		...(hasVideo
			? {
					background: `url(${MediaIcon}), linear-gradient(
						rgba(36, 36, 36, 0.6), 
						rgba(36, 36, 36, 0.6)
		  ), url('${background}')`,
					backgroundRepeat: 'no-repeat',
					backgroundPosition: 'center 30px, center center, top center',
					backgroundSize: '56px 56px, cover, cover',
			  }
			: {
					background: `linear-gradient(
			rgba(36, 36, 36, 0.6), 
			rgba(36, 36, 36, 0.6)
		  ), url('${background}')`,
					backgroundRepeat: 'no-repeat',
					backgroundPosition: 'center center, top center',
					backgroundSize: 'cover, cover',
			  }),
		'& h2': cx(
			label,
			css({
				color: '#fff',
				marginBottom: '12px',
			})
		),
		'& p': cx(
			headingSmallLight,
			css({
				color: '#fff',
				margin: '0',
			})
		),
		[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
			'& p': {
				display: 'none',
			},
		},
	});

export const blockquoteStyle = cx(
	headingLarge,
	css({
		maxWidth: '1000px',
		padding: '22px 0 0 26px',
		background: `transparent url(${BlockquoteBG}) no-repeat 0 0`,
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: headingMedium,
	})
);

export const innerWrapperStyle = css({
	maxWidth: desktopMaxWidthBreakpoint,
	width: '100%',
	height: '100%',
	margin: '0 auto',
	padding: '0 20px',
	overflowX: 'hidden',
});

export const treatmentBannerStyle = css({
	background: purple,
	position: 'relative',
	width: '100%',
	height: '280px',
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		height: 'auto',
		padding: '30px 0',
	},
});

export const purpleImageStyle = background =>
	css({
		position: 'absolute',
		top: 0,
		right: 0,
		width: '517px',
		height: '100%',
		background: `transparent url(${background}) no-repeat center center`,
		backgroundSize: 'cover',
		[`@media (max-width: ${tabletLandscapeMaxWidthBreakpoint}px)`]: {
			display: 'none',
		},
	});

export const treatmentBannerInnerStyle = css({
	position: 'relative',
	top: '50%',
	transform: 'translateY(-50%)',
	paddingRight: '517px',
	'& > div': cx(
		headingXLarge,
		css({
			color: '#fff',
		})
	),

	'& strong': {
		fontWeight: 'normal',
		color: aqua,
	},
	'& p': {
		margin: 0,
	},
	[`@media (max-width: ${tabletLandscapeMaxWidthBreakpoint}px)`]: {
		paddingRight: '0',
		textAlign: 'center',
	},
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		top: '0',
		transform: 'none',
	},
});

export const aboutCardStyle = colour =>
	css({
		borderRadius: '4px',
		padding: '24px',
		background: `${colour} url(${QuoteBG}) no-repeat 24px 12px`,
		position: 'relative',
		minHeight: '180px',
		'& span': cx(
			textBlock,
			css({
				color: '#fff',
				display: 'block',
				position: 'relative',
				top: '50%',
				transform: 'translateY(-50%)',
			})
		),
	});

export const aboutCardsStyle = cx(
	cardSection(0),
	css({
		marginTop: '60px',
		maxWidth: '944px',
	})
);

export const disorderWrapStyle = css({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	justifyContent: 'space-between',
	alignItems: 'top',
	padding: '0',

	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		margin: '20px 0',
	},
});

export const disorderStyle = color =>
	css({
		display: 'block',
		listStyle: 'none',
		padding: '0',
		margin: '40px 0',
		width: '480px',
		'& h2': cx(
			headingMedium,
			css({
				color: color,
				marginBottom: '6px',
			})
		),

		'& p': {
			margin: '0',
		},
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			margin: '20px 0',
		},
	});

//headingMedium



// WEBPACK FOOTER //
// ./src/page-styles/about-eating-disorders/index.js